import React, { useState, useEffect } from 'react';
import { Users, Shield, Eye, Heart, Star, CheckCircle, Play, Instagram, MessageCircle } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import AppHeader from './AppHeader';
import { Plan, UserType } from '../types/plan';
import { PlanService } from '../services/PlanService';
import { PlanAccessValidator } from '../services/PlanAccessValidator';
import InfoModal from './InfoModal';

const Home = () => {
  // Estado de autenticação
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados para pagamento
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  
  // Estados para planos dinâmicos
  const [participantPlans, setParticipantPlans] = useState<Plan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  // Estado do modal
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'warning' as 'error' | 'warning' | 'info' | 'success',
    redirectTo: ''
  });

  // Buscar planos do Firestore
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const allPlans = await PlanService.fetchActivePlans(db);
        // Apenas planos de paciente
        const plans = allPlans.filter(plan => plan.userType === UserType.PATIENT);
        plans.sort((a, b) => a.value - b.value);
        setParticipantPlans(plans);
      } catch (error) {
        console.error('Erro ao buscar planos:', error);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  // Scroll suave para seção quando há hash na URL
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  // Estado para o conteúdo da página com valores padrão
  const [content, setContent] = useState({
    heroTitle: 'Viva a experiência da Constelação Familiar em Grupo',
    heroSubtitle: 'Agora no ambiente virtual, com a mesma profundidade e transformação do presencial',
    experienceTitle: 'Uma Constelação em Grupo, no conforto da sua casa',
    experienceSubtitle: 'A Constela Brasil é a primeira plataforma do Brasil feita para trazer a mesma vivência e profundidade das constelações em grupo presenciais para o formato online.',
    feature1Title: 'Participação em Vídeo Conferência',
    feature1Text: 'Junto com outros representantes, cada um com seu avatar interativo que você mesmo movimenta',
    feature2Title: 'Avatar Interativo Completo',
    feature2Text: 'Posicionar no campo, se movimentar, virar, sentar, ajoelhar, deitar, cruzar os braços, fechar os olhos',
    feature3Title: 'Campo Morfogenético Virtual',
    feature3Text: 'Sala virtual acolhedora que representa o campo de informação onde a constelação acontece',
    analysisTitle: 'Análise Profissional Completa',
    analysisText: 'O constelador tem acesso à análise do posicionamento de cada participante, algo essencial para compreender a dinâmica e revelar informações profundas sobre o campo familiar do constelado.',
    analysisTagline: '✨ Tecnologia exclusiva para análise sistêmica',
    benefitsTitle: 'Benefícios para você que quer vivenciar essa experiência',
    benefit1Title: 'Mesma Profundidade do Presencial',
    benefit1Text: 'Com a comodidade de estar onde quiser, mantendo toda a essência transformadora',
    benefit2Title: 'Segurança e Sigilo',
    benefit2Text: 'Especialmente para o constelado, que tem diálogo privado com o constelador',
    benefit3Title: 'Participação Ativa',
    benefit3Text: 'Como representante, você movimenta seu avatar e contribui para o campo',
    benefit4Title: 'Consteladores Homologados',
    benefit4Text: 'Garantindo qualidade e ética profissional em cada sessão',
    benefit5Title: 'Experiência Transformadora',
    benefit5Text: 'Oportunidade de olhar para suas questões, padrões e relacionamentos de forma profunda',
    benefit6Title: 'Acesso Global',
    benefit6Text: 'Participe de qualquer lugar do Brasil ou do mundo com conexão à internet',
    faqTitle: 'Perguntas Frequentes',
    faq1Question: '"Mas funciona mesmo online?"',
    faq1Answer: 'Sim! A força da constelação está no campo morfogenético — e ele não depende de estarmos fisicamente no mesmo lugar. Com o recurso de vídeo conferência, avatares e posicionamento no campo, a vivência é tão profunda quanto a presencial.',
    faq2Question: '"Será que vou conseguir sentir e perceber o campo?"',
    faq2Answer: 'Com certeza! Representantes relatam sentir as mesmas sensações físicas e emocionais que sentiriam numa constelação presencial.',
    faq3Question: '"Não conheço ninguém, será que vou me sentir à vontade?"',
    faq3Answer: 'O ambiente virtual é seguro, acolhedor e conduzido por profissionais homologados que cuidam para que todos se sintam respeitados e confortáveis.',
    ctaTitle: 'Sua oportunidade de transformar sua vida agora',
    ctaText: 'Seja como representante ou constelado, a Constela Brasil oferece um ambiente inovador, profundo e seguro para você acessar a força transformadora da Constelação Familiar — de qualquer lugar do Brasil ou do mundo.',
    footerCopyright: '© 2025 Constela Brasil. Todos os direitos reservados.'
  });

  // Buscar conteúdo do Firestore em tempo real
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'siteContent', 'XK9cio21NQLwgjfDUpoI'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setContent(prevContent => ({
            ...prevContent,
            ...data
          }));
        }
      },
      (error) => {
        console.error('Erro ao buscar conteúdo da página:', error);
      }
    );

    // Cleanup: cancelar a inscrição quando o componente for desmontado
    return () => unsubscribe();
  }, []);

  // Função para obter o preço numérico de uma string de preço
  const getNumericPrice = (priceString: string): number => {
    // Remove "R$", espaços, e pontos, substitui vírgula por ponto
    const cleanPrice = priceString.replace(/[R$\s.]/g, '').replace(',', '.');
    return parseFloat(cleanPrice);
  };

  // Função para encontrar um plano pelo ID
  const findPlanById = (planId: string): Plan | null => {
    return participantPlans.find(plan => plan.id === planId) || null;
  };

  // Função de seleção de plano (atualizada para usar checkoutUrl)
  const handlePlanSelection = async (planId: string) => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          from: location, 
          planId: planId 
        } 
      });
      return;
    }

    const selectedPlan = findPlanById(planId);
    if (!selectedPlan) {
      console.error('Plano não encontrado:', planId);
      return;
    }

    // Validar se o usuário pode acessar este plano
    if (user && user.role) {
      const validation = PlanAccessValidator.canAccessPlan(user.role, selectedPlan.userType);
      
      if (!validation.canAccess) {
        setModalState({
          isOpen: true,
          title: validation.title || 'Acesso Negado',
          message: validation.message || '',
          variant: validation.variant || 'warning',
          redirectTo: validation.redirectTo || ''
        });
        return;
      }
    }

    setProcessingPlan(planId);

    try {
      // Redirecionar para a página de checkout com o ID do plano
      navigate(`/checkout?planId=${planId}`);
    } finally {
      setProcessingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F6F1]">
      {/* Header fixo */}
      <AppHeader />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-[#F3F6F1]">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8">
            <img 
              src="/assets/constela.png" 
              alt="Constela Brasil" 
              className="h-24 w-auto mx-auto mb-6"
            />
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3A5859] mb-6 leading-tight">
            {content.heroTitle.split('Constelação Familiar')[0]}
            <span className="text-[#A56096]">Constelação Familiar</span>
            {content.heroTitle.split('Constelação Familiar')[1]}
          </h1>
          
          <p className="text-xl sm:text-2xl text-[#496566] mb-8 max-w-3xl mx-auto">
            {content.heroSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-md mx-auto">
            <Link 
              to="/planos?secao=participante"
              className="w-full sm:w-auto bg-[#A56096] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#945586] transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
            >
              Como funciona?
            </Link>
            
            <Link 
              to="/planos?secao=terapeuta"
              className="w-full sm:w-auto border-2 border-[#A56096] text-[#A56096] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#A56096] hover:text-white transition-all flex items-center justify-center gap-2"
            >
              Sou Constelador
            </Link>
          </div>
        </div>
      </section>

      {/* Experiência Virtual */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A5859] mb-4">
              {content.experienceTitle}
            </h2>
            <p className="text-xl text-[#496566] max-w-4xl mx-auto mb-8">
              {content.experienceSubtitle}
            </p>
            
            {/* Imagem ilustrativa */}
            <div className="flex justify-center mb-12">
              <img 
                src="/assets/imagem_home.png" 
                alt="Ilustração de uma sessão de constelação virtual" 
                 className="max-w-2xl w-full h-auto rounded-2xl shadow-lg border border-gray-200"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-[#A56096]/10 p-3 rounded-full">
                  <Users className="h-6 w-6 text-[#A56096]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#3A5859] mb-2">
                    {content.feature1Title}
                  </h3>
                  <p className="text-[#496566]">
                    {content.feature1Text}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#A56096]/10 p-3 rounded-full">
                  <Play className="h-6 w-6 text-[#A56096]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#3A5859] mb-2">
                    {content.feature2Title}
                  </h3>
                  <p className="text-[#496566]">
                    {content.feature2Text}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-[#A56096]/10 p-3 rounded-full">
                  <Eye className="h-6 w-6 text-[#A56096]" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#3A5859] mb-2">
                    {content.feature3Title}
                  </h3>
                  <p className="text-[#496566]">
                    {content.feature3Text}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#3A5859] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                {content.analysisTitle}
              </h3>
              <p className="text-white/80 mb-6">
                {content.analysisText}
              </p>
              <div className="bg-white/20 rounded-lg p-4">
                <p className="text-sm font-medium">
                  {content.analysisTagline}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="py-16 bg-[#F3F6F1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A5859] mb-4">
              {content.benefitsTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit1Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit1Text}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit2Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit2Text}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit3Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit3Text}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit4Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit4Text}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Star className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit5Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit5Text}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="bg-[#A56096]/10 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-[#A56096]" />
              </div>
              <h3 className="font-bold text-lg text-[#3A5859] mb-3">
                {content.benefit6Title}
              </h3>
              <p className="text-[#496566]">
                {content.benefit6Text}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#3A5859] mb-4">
              {content.faqTitle}
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-[#F3F6F1] p-6 rounded-xl border border-[#E5E9E2]">
              <h3 className="font-bold text-xl text-[#3A5859] mb-3">
                {content.faq1Question}
              </h3>
              <p className="text-[#496566] text-lg">
                {content.faq1Answer}
              </p>
            </div>

            <div className="bg-[#F3F6F1] p-6 rounded-xl border border-[#E5E9E2]">
              <h3 className="font-bold text-xl text-[#3A5859] mb-3">
                {content.faq2Question}
              </h3>
              <p className="text-[#496566] text-lg">
                {content.faq2Answer}
              </p>
            </div>

            <div className="bg-[#F3F6F1] p-6 rounded-xl border border-[#E5E9E2]">
              <h3 className="font-bold text-xl text-[#3A5859] mb-3">
                {content.faq3Question}
              </h3>
              <p className="text-[#496566] text-lg">
                {content.faq3Answer}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-16 bg-[#496566] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Valores e Formas de Participar
            </h2>
            <p className="text-xl text-white/80">
              Escolha a modalidade que mais se adequa ao seu momento
            </p>
          </div>

          {loadingPlans ? (
            <div className="text-center py-12">
              <p className="text-white/80">Carregando planos...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {participantPlans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`rounded-2xl p-8 relative flex flex-col ${
                    plan.isFeatured 
                      ? 'bg-[#597879] border-2 border-yellow-400 hover:scale-105 transition-transform' 
                      : 'bg-[#3A5859] border border-white/10 hover:bg-[#496566] transition-colors'
                  }`}
                >
                  {plan.isFeatured && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap">
                        MAIS POPULAR
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <div className="text-4xl font-bold mb-2 text-white">
                      {PlanService.formatCurrency(plan.value)}
                    </div>
                    <p className="text-white/80">{PlanService.formatPeriod(plan.quantity, plan.billingType, plan.isRecurring)}</p>
                  </div>
                  
                  <p className="text-white/80 mb-6 text-center whitespace-pre-line">
                    {plan.description}
                  </p>
                  
                  {plan.features && plan.features.length > 0 && (
                    <ul className="mb-6 space-y-2 text-sm flex-grow text-left">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-white" />
                          <span className="text-white/90">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  <button 
                    onClick={() => handlePlanSelection(plan.id || '')}
                    disabled={processingPlan === plan.id}
                    className={`w-full mt-auto py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait ${
                      plan.isFeatured
                        ? 'bg-white text-[#3A5859] hover:bg-gray-200 font-bold'
                        : 'bg-[#A56096] text-white hover:bg-[#945586]'
                    }`}
                  >
                    {processingPlan === plan.id ? 'Aguarde...' : PlanService.getButtonText(plan.isRecurring)}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-[#3A5859]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {content.ctaTitle}
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            {content.ctaText}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-lg mx-auto">
            <Link 
              to="/planos"
              className="w-full sm:w-auto bg-white text-[#3A5859] px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
            >
              Começar Agora
            </Link>
            
            <Link 
              to="/planos?secao=terapeuta"
              className="w-full sm:w-auto border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#3A5859] transition-all flex items-center justify-center gap-2"
            >
              Sou Constelador
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3A5859] text-white py-12">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-8">
      <div className="flex justify-center items-center space-x-6 mb-6">
        <a
          href="https://instagram.com/constelabrasiloficial"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
          aria-label="Siga-nos no Instagram"
        >
          <Instagram className="w-5 h-5 group-hover:scale-110 transition-transform" />
          <span className="hover:underline">@constelabrasiloficial</span>
        </a>
        <a
          href="https://api.whatsapp.com/send/?phone=5513997701755&text=Ola"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
          aria-label="Fale conosco no WhatsApp"
        >
          <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="hover:underline">WhatsApp</span>
        </a>
      </div>
      <div className="flex justify-center space-x-6 mb-4">
        <Link 
          to="/politica-de-privacidade" 
          className="text-white/80 hover:text-white transition-colors hover:underline"
        >
          Política de Privacidade
        </Link>
        <Link 
          to="/termos-de-servico" 
          className="text-white/80 hover:text-white transition-colors hover:underline"
        >
          Termos de Serviço
        </Link>
      </div>
      <p className="text-white/60">
        {content.footerCopyright}
      </p>
    </div>
  </div>
</footer>

      <InfoModal
        isOpen={modalState.isOpen}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          if (modalState.redirectTo) {
            navigate(modalState.redirectTo);
          }
        }}
        title={modalState.title}
        message={modalState.message}
        variant={modalState.variant}
        buttonText="Entendi"
      />
    </div>
  );
};

export default Home;